/**
 * Progress Dashboard Service
 * API service for fetching progress dashboard data with mock data fallback.
 *
 * Strategy: Each public function attempts to call the real backend API first.
 * If the API call fails (network error, 4xx, 5xx), it falls back to locally
 * generated mock data so the UI always has something to render.
 */

import { API_BASE_URL } from '@/config/env'
import { apiRequest } from '@/services/api/interceptor'
import { getMySkillsV2 } from '@/services/skills/api'
import type { SkillV2DTO } from '@/types/skill'
import type {
  ProgressDashboardData,
  ProgressOverviewStats,
  LearningActivityData,
  LearningActivityDataPoint,
  ActivityTimePeriod,
  CourseProgressItem,
  SkillProgressItem,
  LearningGoal,
  StreakInfo,
  Achievement,
  Milestone,
} from '@/types/progress-dashboard'


// ============================================================================
// Backend API Response Types (match Java DTOs)
// ============================================================================

/** Maps to ProgressOverviewResponse.java */
interface ApiProgressOverview {
  totalLearningTimeMinutes?: number
  totalLearningTimeFormatted?: string
  currentStreak?: number
  longestStreak?: number
  totalCoursesEnrolled?: number
  coursesCompleted?: number
  coursesInProgress?: number
  lessonsCompleted?: number
  quizzesPassed?: number
  assignmentsSubmitted?: number
  overallProgressPercentage?: number
  certificatesEarned?: number
  badgesEarned?: number
  pointsEarned?: number
  userLevel?: string
  pointsToNextLevel?: number
  skillsProgress?: Record<string, number>
  lastActivityAt?: string
  memberSince?: string
}

/** Maps to DashboardDataResponse.StreakInfo */
interface ApiStreakInfo {
  currentStreak?: number
  longestStreak?: number
  totalStreakDays?: number
  todayCompleted?: boolean
  minutesToday?: number
  dailyGoalMinutes?: number
  streakFreezeAvailable?: boolean
  lastActivityTime?: string
}

/** Maps to WeeklyProgressResponse.java */
interface ApiWeeklyProgress {
  weekStartDate?: string
  weekEndDate?: string
  totalMinutesThisWeek?: number
  averageDailyMinutes?: number
  daysActive?: number
  lessonsCompletedThisWeek?: number
  quizzesPassedThisWeek?: number
  weekOverWeekGrowth?: number
  weeklyGoalProgress?: number
  dailyProgress?: ApiDailyProgress[]
  mostProductiveDay?: string
  mostProductiveTime?: string
  coursesStudied?: ApiCourseProgress[]
  skillsPracticed?: Record<string, number>
}

interface ApiDailyProgress {
  date?: string
  dayOfWeek?: string
  learningMinutes?: number
  lessonsCompleted?: number
  quizzesTaken?: number
  goalMet?: boolean
}

interface ApiCourseProgress {
  courseId?: string
  courseTitle?: string
  minutesSpent?: number
  lessonsCompleted?: number
  progressPercentage?: number
  progressIncrease?: number
}

/** Maps to AchievementResponse.java */
interface ApiAchievementResponse {
  totalAchievements?: number
  totalPoints?: number
  userLevel?: string
  userTier?: string
  badges?: ApiBadge[]
  milestones?: ApiMilestone[]
  certificates?: ApiCertificate[]
  recentAchievements?: ApiAchievement[]
  upcomingAchievements?: ApiUpcomingAchievement[]
  leaderboardPosition?: number
  totalUsers?: number
}

interface ApiBadge {
  id?: string
  name?: string
  description?: string
  category?: string
  imageUrl?: string
  rarity?: string
  points?: number
  earnedAt?: string
  earnedFor?: string
}

interface ApiMilestone {
  id?: string
  title?: string
  description?: string
  type?: string
  target?: number
  current?: number
  completed?: boolean
  completedAt?: string
  rewardPoints?: number
  rewardBadgeId?: string
}

interface ApiCertificate {
  id?: string
  courseId?: string
  courseTitle?: string
  certificateUrl?: string
  verificationCode?: string
  issuedAt?: string
  expiresAt?: string
  score?: number
  grade?: string
  verified?: boolean
}

interface ApiAchievement {
  id?: string
  type?: string
  title?: string
  description?: string
  icon?: string
  points?: number
  earnedAt?: string
  shareUrl?: string
}

interface ApiUpcomingAchievement {
  id?: string
  title?: string
  description?: string
  requirement?: string
  currentProgress?: number
  targetProgress?: number
  progressPercentage?: number
  rewardPoints?: number
  hint?: string
}

/** Maps to CourseProgressListResponse.java */
interface ApiCourseProgressItem {
  id?: string
  title?: string
  description?: string
  progress?: number
  status?: string
  totalTopics?: number
  completedTopics?: number
  totalModules?: number
  completedModules?: number
  estimatedTimeToComplete?: number
  lastActivityDate?: string
  category?: string
  difficulty?: string
}


// ============================================================================
// Backend-to-Frontend Mapping Helpers
// ============================================================================

/**
 * Map the backend ProgressOverviewResponse + StreakInfo into the frontend
 * ProgressOverviewStats shape.
 */
function mapOverviewFromApi(
  overview: ApiProgressOverview,
  streak: ApiStreakInfo
): ProgressOverviewStats {
  const skillsCount = overview.skillsProgress
    ? Object.keys(overview.skillsProgress).length
    : 0

  return {
    totalLearningTimeMinutes: overview.totalLearningTimeMinutes ?? 0,
    coursesCompleted: overview.coursesCompleted ?? 0,
    currentStreak: streak.currentStreak ?? overview.currentStreak ?? 0,
    skillsAcquired: skillsCount,
    longestStreak: streak.longestStreak ?? overview.longestStreak ?? 0,
    totalCourses: overview.totalCoursesEnrolled ?? 0,
    coursesInProgress: overview.coursesInProgress ?? 0,
    averageQuizScore: 0, // Not directly available from overview; could be fetched separately
    certificatesEarned: overview.certificatesEarned ?? 0,
  }
}

/**
 * Map the backend StreakInfo (from DashboardController) into the frontend
 * StreakInfo shape.
 */
function mapStreakFromApi(apiStreak: ApiStreakInfo): StreakInfo {
  return {
    currentStreak: apiStreak.currentStreak ?? 0,
    longestStreak: apiStreak.longestStreak ?? 0,
    lastActivityDate: apiStreak.lastActivityTime ?? new Date().toISOString(),
    streakGoal: apiStreak.dailyGoalMinutes ?? 30,
    isStreakActive: apiStreak.todayCompleted ?? false,
    streakFreezeAvailable: apiStreak.streakFreezeAvailable ?? false,
    freezesUsedThisMonth: 0, // Not returned by backend; default to 0
    minutesToday: apiStreak.minutesToday ?? 0,
    dailyGoalMinutes: apiStreak.dailyGoalMinutes ?? 30,
  }
}

/**
 * Map the backend AchievementResponse into the frontend Achievement[] shape.
 * Merges badges, recentAchievements, and upcomingAchievements into a single
 * list using the frontend Achievement interface.
 */
function mapAchievementsFromApi(apiResponse: ApiAchievementResponse): Achievement[] {
  const achievements: Achievement[] = []

  // Convert earned badges into Achievement objects
  if (apiResponse.badges) {
    for (const badge of apiResponse.badges) {
      achievements.push({
        id: badge.id ?? `badge-${achievements.length}`,
        title: badge.name ?? 'Unknown Badge',
        description: badge.description ?? '',
        iconName: badge.imageUrl ?? 'Award',
        rarity: normalizeRarity(badge.rarity),
        earnedAt: badge.earnedAt,
        points: badge.points ?? 0,
        category: badge.category ?? 'General',
        requirement: badge.earnedFor ?? '',
      })
    }
  }

  // Convert recent achievements
  if (apiResponse.recentAchievements) {
    for (const ach of apiResponse.recentAchievements) {
      // Avoid duplicates if a badge already covers this achievement
      if (achievements.some(a => a.id === ach.id)) continue
      achievements.push({
        id: ach.id ?? `ach-${achievements.length}`,
        title: ach.title ?? 'Achievement',
        description: ach.description ?? '',
        iconName: ach.icon ?? 'Trophy',
        rarity: 'uncommon',
        earnedAt: ach.earnedAt,
        points: ach.points ?? 0,
        category: ach.type ?? 'General',
        requirement: '',
      })
    }
  }

  // Convert upcoming achievements (not yet earned, with progress)
  if (apiResponse.upcomingAchievements) {
    for (const upcoming of apiResponse.upcomingAchievements) {
      if (achievements.some(a => a.id === upcoming.id)) continue
      achievements.push({
        id: upcoming.id ?? `upcoming-${achievements.length}`,
        title: upcoming.title ?? 'Upcoming Achievement',
        description: upcoming.description ?? '',
        iconName: 'Star',
        rarity: 'common',
        progress: Math.round(upcoming.progressPercentage ?? 0),
        points: upcoming.rewardPoints ?? 0,
        category: 'Upcoming',
        requirement: upcoming.requirement ?? '',
      })
    }
  }

  return achievements
}

/**
 * Map the backend AchievementResponse.Milestone[] into the frontend Milestone[] shape.
 */
function mapMilestonesFromApi(apiResponse: ApiAchievementResponse): Milestone[] {
  if (!apiResponse.milestones) return []

  return apiResponse.milestones.map((m, index) => ({
    id: m.id ?? `mile-${index}`,
    title: m.title ?? 'Milestone',
    description: m.description ?? '',
    targetValue: m.target ?? 0,
    currentValue: m.current ?? 0,
    unit: mapMilestoneTypeToUnit(m.type),
    isCompleted: m.completed ?? false,
    completedAt: m.completedAt,
    reward: m.rewardBadgeId ? `Badge: ${m.rewardBadgeId}` : undefined,
    iconName: mapMilestoneTypeToIcon(m.type),
  }))
}

/**
 * Map the backend WeeklyProgressResponse into the frontend LearningActivityData shape.
 * Generates smart labels based on the period:
 * - thisWeek: "Mon", "Tue", etc.
 * - thisMonth: "Mar 1", "Mar 2", etc.
 * - allTime (weekly buckets): "Feb 17", "Feb 24", etc.
 */
function mapWeeklyProgressFromApi(
  apiWeekly: ApiWeeklyProgress,
  period: ActivityTimePeriod
): LearningActivityData {
  const dataPoints: LearningActivityDataPoint[] = []

  if (apiWeekly.dailyProgress) {
    for (const day of apiWeekly.dailyProgress) {
      const dateStr = day.date ?? new Date().toISOString()
      const label = formatDataPointLabel(dateStr, day.dayOfWeek, period)

      dataPoints.push({
        date: dateStr,
        label,
        minutesLearned: day.learningMinutes ?? 0,
        lessonsCompleted: day.lessonsCompleted ?? 0,
        quizzesPassed: day.quizzesTaken ?? 0,
      })
    }
  }

  const totalMinutes = apiWeekly.totalMinutesThisWeek ?? dataPoints.reduce((sum, dp) => sum + dp.minutesLearned, 0)
  const avgMinutes = apiWeekly.averageDailyMinutes ?? (dataPoints.length > 0 ? Math.round(totalMinutes / dataPoints.length) : 0)

  // Format mostActiveDay for display
  let mostActiveDay = apiWeekly.mostProductiveDay ?? ''
  if (mostActiveDay && period === 'thisWeek') {
    mostActiveDay = mostActiveDay.charAt(0) + mostActiveDay.slice(1).toLowerCase()
  } else if (mostActiveDay && period === 'allTime') {
    // For all time, mostProductiveDay is a date string like "2026-02-17"
    const d = new Date(mostActiveDay + 'T00:00:00')
    if (!isNaN(d.getTime())) {
      mostActiveDay = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    }
  }

  return {
    period,
    dataPoints,
    totalMinutes,
    averageMinutesPerDay: avgMinutes,
    mostActiveDay,
  }
}

function formatDataPointLabel(dateStr: string, dayOfWeek: string | undefined, period: ActivityTimePeriod): string {
  const d = new Date(dateStr + 'T00:00:00')
  if (isNaN(d.getTime())) return dayOfWeek ?? ''

  if (period === 'thisWeek') {
    return d.toLocaleDateString(undefined, { weekday: 'short' })
  }
  if (period === 'thisMonth') {
    return d.toLocaleDateString(undefined, { day: 'numeric' })
  }
  // allTime — weekly buckets show "MMM D"
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}


/**
 * Map SkillV2DTO[] (from /my-skills/v2) into SkillProgressItem[].
 * This is the authoritative skills source with actual proficiency data from resume analysis.
 */
function mapSkillsV2ToProgressItems(skills: SkillV2DTO[]): SkillProgressItem[] {
  return skills.map((skill, index) => {
    const proficiencyLevel = mapProficiencyFromPercentage(skill.proficiencyLevel)
    const progressPct = skill.proficiencyLevel ?? 0

    // XP derived from proficiency percentage (no real XP system yet)
    const pointsEarned = Math.round(progressPct)
    const pointsToNext = pointsEarned > 0 ? 100 : 0

    return {
      id: skill.id ?? `skill-${index}`,
      name: skill.name ?? 'Unknown Skill',
      category: skill.domain ?? 'General',
      proficiencyLevel,
      progressToNextLevel: progressPct,
      pointsEarned,
      pointsToNextLevel: pointsToNext,
      lastPracticed: skill.updatedAt ?? skill.createdAt ?? new Date().toISOString(),
      relatedCourses: [],
      iconName: skill.icon ?? 'Code2',
    }
  })
}

/**
 * Convert a numeric proficiency percentage (0-100) to a proficiency level label.
 */
function mapProficiencyFromPercentage(pct: number): 'novice' | 'beginner' | 'intermediate' | 'advanced' | 'expert' {
  if (pct >= 80) return 'expert'
  if (pct >= 60) return 'advanced'
  if (pct >= 40) return 'intermediate'
  if (pct >= 20) return 'beginner'
  return 'novice'
}

function mapCourseProgressFromApi(item: ApiCourseProgressItem): CourseProgressItem {
  const difficulty = (['beginner', 'intermediate', 'advanced'] as const).includes(
    item.difficulty as 'beginner' | 'intermediate' | 'advanced'
  )
    ? (item.difficulty as 'beginner' | 'intermediate' | 'advanced')
    : 'beginner'

  const status = (['not-started', 'in-progress', 'completed', 'paused'] as const).includes(
    item.status as 'not-started' | 'in-progress' | 'completed' | 'paused'
  )
    ? (item.status as 'not-started' | 'in-progress' | 'completed' | 'paused')
    : 'not-started'

  return {
    id: item.id ?? '',
    title: item.title ?? 'Untitled Course',
    description: item.description ?? '',
    progress: item.progress ?? 0,
    status,
    totalModules: item.totalModules ?? 0,
    completedModules: item.completedModules ?? 0,
    totalTopics: item.totalTopics ?? 0,
    completedTopics: item.completedTopics ?? 0,
    estimatedTimeToComplete: item.estimatedTimeToComplete ?? 0,
    lastActivityDate: item.lastActivityDate ?? new Date().toISOString(),
    category: item.category ?? 'General',
    difficulty,
  }
}

// ============================================================================
// Small Utility Mappers
// ============================================================================

function normalizeRarity(rarity?: string): 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary' {
  const validRarities = ['common', 'uncommon', 'rare', 'epic', 'legendary'] as const
  if (rarity && validRarities.includes(rarity as typeof validRarities[number])) {
    return rarity as typeof validRarities[number]
  }
  return 'common'
}


function mapMilestoneTypeToUnit(type?: string): string {
  switch (type) {
    case 'courses_completed': return 'courses'
    case 'hours_learned': return 'hours'
    case 'streak_days': return 'days'
    default: return 'units'
  }
}

function mapMilestoneTypeToIcon(type?: string): string {
  switch (type) {
    case 'courses_completed': return 'BookOpen'
    case 'hours_learned': return 'Clock'
    case 'streak_days': return 'Flame'
    default: return 'Star'
  }
}

/**
 * Map frontend ActivityTimePeriod to the backend weekStartDate query parameter.
 * The backend weekly endpoint accepts an optional weekStartDate (YYYY-MM-DD).
 * For "thisWeek" we use the current week start. For "thisMonth" we use the
 * first day of the month. For "allTime" we omit the param (undefined).
 */
function getPeriodDateRange(period: ActivityTimePeriod): { startDate?: string; endDate?: string } {
  const now = new Date()
  if (period === 'thisWeek') {
    const dayOfWeek = now.getDay()
    const weekStart = new Date(now)
    weekStart.setDate(weekStart.getDate() - dayOfWeek)
    return { startDate: formatDate(weekStart) }
  } else if (period === 'thisMonth') {
    return {
      startDate: formatDate(new Date(now.getFullYear(), now.getMonth(), 1)),
      endDate: formatDate(now),
    }
  }
  return {}
}

function formatDate(d: Date): string {
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// ============================================================================
// Mock Data Generation (only for endpoints without real backend support)
// ============================================================================

/**
 * Derive learning goals from real data (streak, weekly activity, and enrolled courses).
 * No hardcoded/mock data — all values come from actual API responses.
 */
function deriveGoalsFromRealData(
  streakInfo: StreakInfo,
  activityData: LearningActivityData,
  courseProgress: CourseProgressItem[],
): LearningGoal[] {
  const goals: LearningGoal[] = []

  // Daily Learning goal — derived from real streak data
  goals.push({
    id: 'goal-daily',
    title: 'Daily Learning',
    type: 'daily',
    category: 'time',
    targetValue: streakInfo.dailyGoalMinutes,
    currentValue: Math.min(streakInfo.minutesToday, streakInfo.dailyGoalMinutes),
    unit: 'minutes',
    startDate: new Date().toISOString(),
    isCompleted: streakInfo.minutesToday >= streakInfo.dailyGoalMinutes,
  })

  // Learning Streak goal — derived from real streak data
  goals.push({
    id: 'goal-streak',
    title: 'Learning Streak',
    type: 'custom',
    category: 'streak',
    targetValue: streakInfo.streakGoal,
    currentValue: streakInfo.currentStreak,
    unit: 'days',
    startDate: new Date().toISOString(),
    isCompleted: streakInfo.currentStreak >= streakInfo.streakGoal,
    streakDays: streakInfo.currentStreak,
  })

  // Weekly Lessons goal — derived from real weekly activity data
  const lessonsThisWeek = activityData.dataPoints.reduce(
    (sum, dp) => sum + (dp.lessonsCompleted ?? 0),
    0,
  )
  goals.push({
    id: 'goal-weekly',
    title: 'Weekly Lessons',
    type: 'weekly',
    category: 'lessons',
    targetValue: 10,
    currentValue: lessonsThisWeek,
    unit: 'lessons',
    startDate: activityData.dataPoints[0]?.date ?? new Date().toISOString(),
    isCompleted: lessonsThisWeek >= 10,
  })

  // Course completion goals — one per in-progress course (real titles, real progress)
  const inProgressCourses = courseProgress.filter(c => c.status === 'in-progress')
  for (const course of inProgressCourses) {
    goals.push({
      id: `goal-course-${course.id}`,
      title: `Complete ${course.title}`,
      type: 'custom',
      category: 'courses',
      targetValue: 100,
      currentValue: course.progress,
      unit: 'percent',
      startDate: course.lastActivityDate ?? new Date().toISOString(),
      isCompleted: course.progress >= 100,
    })
  }

  return goals
}

// ============================================================================
// API Functions (real API with empty-state fallback)
// ============================================================================

/**
 * Fetch complete progress dashboard data.
 *
 * Makes parallel calls to:
 * - GET /api/core/progress/overview
 * - GET /api/core/dashboard/streak
 * - GET /api/core/progress/achievements
 * - GET /api/v1/my-skills/v2 (authoritative skills data with proficiency from resume analysis)
 *
 * Falls back to mock data if any API call fails.
 */
export async function getProgressDashboardData(): Promise<ProgressDashboardData> {
  try {
    const [overviewData, streakData, achievementsData, mySkillsV2Data] = await Promise.all([
      apiRequest<ApiProgressOverview>(`${API_BASE_URL}/api/core/progress/overview`, { method: 'GET' }),
      apiRequest<ApiStreakInfo>(`${API_BASE_URL}/api/core/dashboard/streak`, { method: 'GET' }),
      apiRequest<ApiAchievementResponse>(`${API_BASE_URL}/api/core/progress/achievements`, { method: 'GET' }),
      getMySkillsV2().catch(() => null),
    ])

    const overviewStats = mapOverviewFromApi(overviewData, streakData)
    const streakInfo = mapStreakFromApi(streakData)
    const achievements = mapAchievementsFromApi(achievementsData)
    const milestones = mapMilestonesFromApi(achievementsData)
    const skillsProgress = mySkillsV2Data?.skills
      ? mapSkillsV2ToProgressItems(mySkillsV2Data.skills)
      : []

    // Use V2 skills count for accurate skillsAcquired stat
    if (mySkillsV2Data?.stats) {
      overviewStats.skillsAcquired = mySkillsV2Data.stats.learnedSkills ?? mySkillsV2Data.skills.length
    }

    // Derive recent achievements from the earned ones, sorted by date
    const recentAchievements = achievements
      .filter(a => a.earnedAt)
      .sort((a, b) => new Date(b.earnedAt!).getTime() - new Date(a.earnedAt!).getTime())
      .slice(0, 3)

    // Fetch weekly activity data for the default "thisWeek" view
    let activityData: LearningActivityData
    try {
      const { startDate, endDate } = getPeriodDateRange('thisWeek')
      const params = new URLSearchParams()
      if (startDate) params.set('weekStartDate', startDate)
      if (endDate) params.set('endDate', endDate)
      const queryString = params.toString()
      const weeklyUrl = queryString
        ? `${API_BASE_URL}/api/core/progress/weekly?${queryString}`
        : `${API_BASE_URL}/api/core/progress/weekly`
      const weeklyData = await apiRequest<ApiWeeklyProgress>(weeklyUrl, { method: 'GET' })
      activityData = mapWeeklyProgressFromApi(weeklyData, 'thisWeek')
    } catch {
      activityData = {
        period: 'thisWeek',
        dataPoints: [],
        totalMinutes: 0,
        averageMinutesPerDay: 0,
        mostActiveDay: '',
      }
    }

    // Fetch course progress from real endpoint (falls back to mock internally)
    const courseProgress = await getCourseProgressList()

    return {
      overviewStats,
      activityData,
      courseProgress,
      skillsProgress,
      goals: deriveGoalsFromRealData(streakInfo, activityData, courseProgress),
      streakInfo,
      achievements,
      milestones,
      recentAchievements,
    }
  } catch (error) {
    console.warn('Progress dashboard API call failed, returning empty state:', error)
    return {
      overviewStats: {
        totalLearningTimeMinutes: 0,
        coursesCompleted: 0,
        currentStreak: 0,
        skillsAcquired: 0,
        longestStreak: 0,
        totalCourses: 0,
        coursesInProgress: 0,
        averageQuizScore: 0,
        certificatesEarned: 0,
      },
      activityData: {
        period: 'thisWeek',
        dataPoints: [],
        totalMinutes: 0,
        averageMinutesPerDay: 0,
        mostActiveDay: '',
      },
      courseProgress: [],
      skillsProgress: [],
      goals: [],
      streakInfo: {
        currentStreak: 0,
        longestStreak: 0,
        lastActivityDate: new Date().toISOString(),
        streakGoal: 7,
        isStreakActive: false,
        streakFreezeAvailable: false,
        freezesUsedThisMonth: 0,
        minutesToday: 0,
        dailyGoalMinutes: 30,
      },
      achievements: [],
      milestones: [],
      recentAchievements: [],
    }
  }
}

/**
 * Fetch learning activity data for a specific time period.
 *
 * Calls GET /api/core/progress/weekly with an appropriate weekStartDate param.
 * Falls back to mock data if the call fails.
 */
export async function getLearningActivityData(period: ActivityTimePeriod): Promise<LearningActivityData> {
  try {
    const { startDate, endDate } = getPeriodDateRange(period)
    const params = new URLSearchParams()
    if (startDate) params.set('weekStartDate', startDate)
    if (endDate) params.set('endDate', endDate)
    const queryString = params.toString()
    const url = queryString
      ? `${API_BASE_URL}/api/core/progress/weekly?${queryString}`
      : `${API_BASE_URL}/api/core/progress/weekly`

    const weeklyData = await apiRequest<ApiWeeklyProgress>(url, { method: 'GET' })
    return mapWeeklyProgressFromApi(weeklyData, period)
  } catch (error) {
    console.warn(`Weekly progress API call failed for period "${period}", returning empty data:`, error)
    return {
      period,
      dataPoints: [],
      totalMinutes: 0,
      averageMinutesPerDay: 0,
      mostActiveDay: '',
    }
  }
}

/**
 * Fetch overview stats.
 * Calls GET /api/core/progress/overview and GET /api/core/dashboard/streak.
 * Falls back to mock data if the call fails.
 */
export async function getOverviewStats(): Promise<ProgressOverviewStats> {
  try {
    const [overviewData, streakData] = await Promise.all([
      apiRequest<ApiProgressOverview>(`${API_BASE_URL}/api/core/progress/overview`, { method: 'GET' }),
      apiRequest<ApiStreakInfo>(`${API_BASE_URL}/api/core/dashboard/streak`, { method: 'GET' }),
    ])
    return mapOverviewFromApi(overviewData, streakData)
  } catch (error) {
    console.warn('Overview stats API call failed, returning zeroed stats:', error)
    return {
      totalLearningTimeMinutes: 0,
      coursesCompleted: 0,
      currentStreak: 0,
      skillsAcquired: 0,
      longestStreak: 0,
      totalCourses: 0,
      coursesInProgress: 0,
      averageQuizScore: 0,
      certificatesEarned: 0,
    }
  }
}

/**
 * Fetch course progress.
 * Calls GET /api/core/progress/courses. Falls back to mock data.
 */
export async function getCourseProgressList(): Promise<CourseProgressItem[]> {
  try {
    const data = await apiRequest<ApiCourseProgressItem[]>(
      `${API_BASE_URL}/api/core/progress/courses`,
      { method: 'GET' }
    )
    return data.map(mapCourseProgressFromApi)
  } catch (error) {
    console.warn('Course progress API call failed, returning empty list:', error)
    return []
  }
}

/**
 * Fetch skills progress.
 * Uses the V2 skills API (/my-skills/v2) which has actual proficiency data from resume analysis.
 */
export async function getSkillsProgressList(): Promise<SkillProgressItem[]> {
  try {
    const mySkillsData = await getMySkillsV2()
    return mapSkillsV2ToProgressItems(mySkillsData.skills)
  } catch (error) {
    console.warn('Skills progress API call failed, returning empty list:', error)
    return []
  }
}

/**
 * Fetch learning goals derived from real progress data.
 */
export async function getLearningGoals(): Promise<LearningGoal[]> {
  try {
    const dashboardData = await getProgressDashboardData()
    return dashboardData.goals
  } catch {
    return []
  }
}

/**
 * Fetch streak info.
 * Calls GET /api/core/dashboard/streak. Falls back to mock data.
 */
export async function getStreakInfo(): Promise<StreakInfo> {
  try {
    const streakData = await apiRequest<ApiStreakInfo>(
      `${API_BASE_URL}/api/core/dashboard/streak`,
      { method: 'GET' }
    )
    return mapStreakFromApi(streakData)
  } catch (error) {
    console.warn('Streak info API call failed, returning empty streak:', error)
    return {
      currentStreak: 0,
      longestStreak: 0,
      lastActivityDate: new Date().toISOString(),
      streakGoal: 7,
      isStreakActive: false,
      streakFreezeAvailable: false,
      freezesUsedThisMonth: 0,
      minutesToday: 0,
      dailyGoalMinutes: 30,
    }
  }
}

/**
 * Update the user's daily learning goal.
 * Calls PUT /api/core/user/settings to persist the new value.
 */
export async function updateDailyGoal(minutes: number): Promise<void> {
  await apiRequest(`${API_BASE_URL}/api/core/user/settings`, {
    method: 'PUT',
    body: JSON.stringify({ learning: { dailyGoalMinutes: minutes } }),
  })
}

/**
 * Fetch achievements.
 * Calls GET /api/core/progress/achievements. Falls back to mock data.
 */
export async function getAchievements(): Promise<Achievement[]> {
  try {
    const achievementsData = await apiRequest<ApiAchievementResponse>(
      `${API_BASE_URL}/api/core/progress/achievements`,
      { method: 'GET' }
    )
    return mapAchievementsFromApi(achievementsData)
  } catch (error) {
    console.warn('Achievements API call failed, returning empty list:', error)
    return []
  }
}

/**
 * Fetch milestones.
 * Calls GET /api/core/progress/achievements (milestones are nested).
 * Falls back to mock data.
 */
export async function getMilestones(): Promise<Milestone[]> {
  try {
    const achievementsData = await apiRequest<ApiAchievementResponse>(
      `${API_BASE_URL}/api/core/progress/achievements`,
      { method: 'GET' }
    )
    return mapMilestonesFromApi(achievementsData)
  } catch (error) {
    console.warn('Milestones API call failed, returning empty list:', error)
    return []
  }
}

// Default export
const progressDashboardService = {
  getProgressDashboardData,
  getLearningActivityData,
  getOverviewStats,
  getCourseProgressList,
  getSkillsProgressList,
  getLearningGoals,
  getStreakInfo,
  getAchievements,
  getMilestones,
}

export default progressDashboardService
